import React, { useState, useCallback } from 'react';
import { IFileTrashData } from 'features/file-manager/utils/file-manager';
import { TrashFilesListView } from 'features/file-manager/components/trash/trash-files-list-view';
import { TrashGridView } from 'features/file-manager/components/trash/trash-files-grid-view';
import { TrashFilters } from 'features/file-manager/types/header-toolbar.type';
import { TrashHeaderToolbar } from 'features/file-manager/components/trash/trash-files-header-toolbar';
import { useHardDeleteDocumentFile, useUpdateFileSoftDeletedStatus } from 'features/file-manager/hooks/use-document-manager';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import { useFileFilters } from 'features/file-manager/hooks/use-file-filters';

interface TrashProps {
  onRestoreFile?: (file: IFileTrashData) => void;
  readonly onPermanentDelete?: (file: IFileTrashData) => void;
  onClearTrash?: () => void;
  showTitle?: boolean;
  onClearTrashReady?: (handler: () => Promise<void>) => void;
  onAllFileIdsChange?: (ids: string[]) => void;
}

const Trash: React.FC<TrashProps> = ({ showTitle = true, onClearTrashReady, onAllFileIdsChange }) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [allFileIds, setAllFileIds] = useState<string[]>([]);
  const { mutateAsync: mutatePermanentDeleteDocumentFiles } = useHardDeleteDocumentFile();
  const { mutateAsync: mutateUpdateFileSoftDeletedStatus } = useUpdateFileSoftDeletedStatus();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { filters, handleFiltersChange } = useFileFilters<TrashFilters>({
    name: '',
    fileType: undefined,
    deletedBy: undefined,
    trashedDate: undefined,
  });

  const [deletedItemIds, setDeletedItemIds] = useState<Set<string>>(new Set());
  const [restoredItemIds, setRestoredItemIds] = useState<Set<string>>(new Set());

  const handleRestoreFile = useCallback(
    async (file: IFileTrashData) => {
      setRestoredItemIds((prev) => new Set([...Array.from(prev), file.id]));

      setSelectedItems((prev) => prev.filter((id) => id !== file.id));

      try {
        await mutateUpdateFileSoftDeletedStatus({ fileId: file.id, softDeleted: false }, {
          onSuccess: () => {
            toast({
              variant: 'success',
              title: t('SUCCESS'),
              description: t('FILE_RESTORED_SUCCESSFULLY'),
            });
            // queryClient.invalidateQueries({ queryKey: ['trashed-files'] });
          },
          onError: () => {
            toast({
              variant: 'destructive',
              title: t('ERROR'),
              description: t('FILE_RESTORED_FAILED'),
            });
          }
        });
      } catch (error) {
        console.error('Error restoring file:', error);
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: t('FILE_RESTORED_FAILED'),
        });
      }
    },
    [mutateUpdateFileSoftDeletedStatus]
  );

  const handlePermanentDelete = useCallback(
    async (file: IFileTrashData) => {
      setDeletedItemIds((prev) => new Set([...Array.from(prev), file.id]));

      setSelectedItems((prev) => prev.filter((id) => id !== file.id));

      try {
        await mutatePermanentDeleteDocumentFiles({ fileIds: [file.id], queryKeysToInvalidate: [['trashed-files']] }, {
          onSuccess: () => {
            toast({
              variant: 'success',
              title: t('SUCCESS'),
              description: t('FILE_DELETED_PERMANENTLY'),
            });
          },
          onError: () => {
            toast({
              variant: 'destructive',
              title: t('ERROR'),
              description: t('FILE_DELETED_FAILED'),
            });
          }
        });
      } catch (error) {
        console.error('Error deleting file:', error);
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: t('FILE_DELETED_FAILED'),
        });
      }
    },
    [mutatePermanentDeleteDocumentFiles]
  );

  const handleRestoreSelected = useCallback(() => {
    setRestoredItemIds((prev) => new Set([...Array.from(prev), ...selectedItems]));
    setSelectedItems([]);
  }, [selectedItems]);

  const handleClearTrash = useCallback(async () => {
    try {
      await mutatePermanentDeleteDocumentFiles({ fileIds: allFileIds, queryKeysToInvalidate: [['trashed-files']] }, {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: t('TRASH_CLEARED_SUCCESSFULLY'),
          });
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: t('ERROR'),
            description: t('TRASH_CLEARED_FAILED'),
          });
        }
      });
      setSelectedItems([]);
    } catch (error) {
      console.error('Error deleting files:', error);
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('TRASH_CLEARED_FAILED'),
      });
    }
  }, [allFileIds, mutatePermanentDeleteDocumentFiles]);

  const handleViewModeChange = useCallback((mode: string) => {
    setViewMode(mode as 'grid' | 'list');
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    handleFiltersChange({
      ...filters,
      name: query,
    });
  }, []);

  // Expose the clear trash handler to parent when component mounts
  React.useEffect(() => {
    if (onClearTrashReady) {
      onClearTrashReady(handleClearTrash);
    }
  }, [onClearTrashReady, handleClearTrash]);

  // Notify parent when allFileIds changes
  React.useEffect(() => {
    if (onAllFileIdsChange) {
      onAllFileIdsChange(allFileIds);
    }
  }, [allFileIds, onAllFileIdsChange]);

  const commonViewProps = {
    onRestore: handleRestoreFile,
    onDelete: handlePermanentDelete,
    onPermanentDelete: handlePermanentDelete,
    filters,
    selectedItems,
    onSelectionChange: setSelectedItems,
    deletedItemIds,
    restoredItemIds,
    handleSetAllFileIds: setAllFileIds,
  };

  return (
    <div className="flex flex-col w-full space-y-4 h-[calc(100vh-60px)]">
      <TrashHeaderToolbar
        viewMode={viewMode}
        handleViewMode={handleViewModeChange}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearTrash={handleClearTrash}
        onRestoreSelected={handleRestoreSelected}
        selectedItems={selectedItems}
        allFileIds={allFileIds}
        showTitle={showTitle}
      />

      <div className="flex-1 overflow-hidden">
        {viewMode === 'grid' ? (
          <div className="h-full overflow-y-auto">
            <TrashGridView {...commonViewProps} />
          </div>
        ) : (
          <div className="h-full">
            <TrashFilesListView {...commonViewProps} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Trash;
